/**
 * Collection Filtering & Sorting
 * Handles product collection browsing with filter drawer and sort controls
 */

class CollectionFilters {
  constructor() {
    this.filterDrawer = document.querySelector('[data-filters]');
    this.filterForm = document.querySelector('#FacetFiltersForm');
    this.filterToggle = document.querySelector('[data-filter-toggle]');
    this.filterCloseButtons = document.querySelectorAll('[data-filter-close]');
    this.sortSelect = document.querySelector('[data-sort-select]');
    this.clearFiltersBtn = document.querySelector('[data-clear-filters]');

    this.filterTimeout = null;

    this.init();
  }

  init() {
    if (!this.filterDrawer) return;

    // Filter drawer toggle
    this.filterToggle?.addEventListener('click', () => this.openFilters());

    // Filter drawer close buttons
    this.filterCloseButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeFilters());
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isFilterDrawerOpen()) {
        this.closeFilters();
      }
    });

    // Sort change
    this.sortSelect?.addEventListener('change', (e) => this.onSortChange(e));

    // Filter form submission
    this.filterForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitFilters();
    });

    // Filter checkbox changes (auto-submit after delay)
    this.filterForm?.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        this.debouncedSubmit();
      }
    });

    // Price range inputs (auto-submit after delay)
    const priceInputs = this.filterForm?.querySelectorAll('.collection-filter__price-input');
    priceInputs?.forEach(input => {
      input.addEventListener('input', () => this.debouncedSubmit());
    });

    // Active filter removal (already have href, just need styling feedback)
    document.querySelectorAll('.active-filter').forEach(filter => {
      filter.addEventListener('click', (e) => {
        // Show loading state
        filter.style.opacity = '0.5';
      });
    });
  }

  openFilters() {
    if (!this.filterDrawer) return;

    this.filterDrawer.classList.add('collection-filters--open');
    this.filterDrawer.setAttribute('aria-hidden', 'false');
    this.filterToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    const firstFocusable = this.filterDrawer.querySelector('button, input, select, textarea, a[href]');
    firstFocusable?.focus();
  }

  closeFilters() {
    if (!this.filterDrawer) return;

    this.filterDrawer.classList.remove('collection-filters--open');
    this.filterDrawer.setAttribute('aria-hidden', 'true');
    this.filterToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // Return focus to toggle button
    this.filterToggle?.focus();
  }

  isFilterDrawerOpen() {
    return this.filterDrawer?.classList.contains('collection-filters--open');
  }

  onSortChange(e) {
    const sortValue = e.target.value;
    const url = new URL(window.location.href);

    if (sortValue === 'manual') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }

    // Preserve page number or reset to 1
    url.searchParams.delete('page');

    window.location.href = url.toString();
  }

  debouncedSubmit() {
    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.submitFilters();
    }, 800);
  }

  submitFilters() {
    if (!this.filterForm) return;

    const formData = new FormData(this.filterForm);
    const url = new URL(window.location.href);

    // Remove existing filter parameters
    const keysToDelete = [];
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Add new filter parameters
    formData.forEach((value, key) => {
      if (value && key.startsWith('filter.')) {
        url.searchParams.append(key, value);
      }
    });

    // Reset to page 1 when filters change
    url.searchParams.delete('page');

    // Navigate to filtered URL
    window.location.href = url.toString();
  }

  clearAllFilters() {
    const url = new URL(window.location.href);

    // Remove all filter parameters
    const keysToDelete = [];
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => url.searchParams.delete(key));

    // Reset to page 1
    url.searchParams.delete('page');

    window.location.href = url.toString();
  }
}

// ============================================
// Collection Product Grid Enhancements
// ============================================

class CollectionGrid {
  constructor() {
    this.gridContainer = document.querySelector('.product-gallery__list');
    this.init();
  }

  init() {
    if (!this.gridContainer) return;

    // Add intersection observer for lazy loading images
    if ('IntersectionObserver' in window) {
      this.setupLazyLoading();
    }
  }

  setupLazyLoading() {
    const images = this.gridContainer.querySelectorAll('img[loading="lazy"]');

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;

          // Trigger load if not already loaded
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }

          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    images.forEach(img => imageObserver.observe(img));
  }

}

// ============================================
// Initialize on DOM ready
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  new CollectionFilters();
  new CollectionGrid();
});

// ============================================
// Accessibility: Focus management
// ============================================

// Trap focus within filter drawer when open
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Tab') return;

  const filterDrawer = document.querySelector('.collection-filters--open');
  if (!filterDrawer) return;

  const focusableElements = filterDrawer.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (e.shiftKey) {
    // Shift + Tab
    if (document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    }
  } else {
    // Tab
    if (document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }
});
